import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, Mic, Volume2, VolumeX } from "lucide-react";
import { useChat } from "../hooks/useChat";
import { useVoice } from "../hooks/useVoice";
import { useSpeech } from "../hooks/useSpeech";
import ChatMessages from "../components/ChatMessages";

const chips = [
  "Help me book a court tonight",
  "Which coach should a beginner pick?",
  "Compare the Ace and Grand Slam tiers",
  "¿Qué canchas tienen luz nocturna?",
  "Explain a tiebreak like I'm five",
  "Quel est le meilleur court en terre battue ?",
];

export default function Assistant() {
  const [input, setInput] = useState("");
  const { messages, sendMessage, streaming } = useChat();
  const { listening, toggle, supported } = useVoice((text) =>
    setInput((prev) => (prev ? prev + " " : "") + text)
  );
  const speech = useSpeech();
  const prevStreaming = useRef(false);

  useEffect(() => {
    if (prevStreaming.current && !streaming && speech.enabled) {
      const last = messages[messages.length - 1];
      if (last?.role === "assistant" && last.content) speech.speak(last.content);
    }
    prevStreaming.current = streaming;
  }, [streaming, messages, speech]);

  const submit = (text) => {
    if (streaming) return;
    speech.stop();
    sendMessage(text);
    setInput("");
  };

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 flex flex-col h-[calc(100vh-5rem)] [height:calc(100dvh-5rem)]" data-testid="assistant-page">
      <div className="flex-1 overflow-y-auto py-10">
        {messages.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="pt-10">
            <div className="w-16 h-16 rounded-full bg-[#ccff00] flex items-center justify-center font-display text-3xl text-black mb-8 glow-pulse">A</div>
            <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              Meet <span className="text-[#ccff00]">Ace.</span>
            </h1>
            <p className="text-white/60 max-w-xl mt-6 leading-relaxed">
              Your universal AI concierge. Ask about courts, coaching and memberships — or anything else, in any language.
            </p>
            <div className="flex flex-wrap gap-3 mt-10">
              {chips.map((c) => (
                <button
                  key={c}
                  onClick={() => submit(c)}
                  data-testid="assistant-prompt-chip"
                  className="text-sm border border-[#ccff00]/40 text-[#ccff00] px-4 py-2.5 rounded-full hover:bg-[#ccff00]/10 hover:-translate-y-0.5 transition-all duration-200 inline-flex items-center gap-2"
                >
                  <Sparkles size={14} /> {c}
                </button>
              ))}
            </div>
            <p className="text-white/30 text-xs mt-12">
              Demo assistant — not medical, financial or legal advice. Bookings made here are simulated.
            </p>
          </motion.div>
        ) : (
          <ChatMessages messages={messages} streaming={streaming} />
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(input);
        }}
        className="pb-6 sm:pb-8 pt-4 flex gap-2 sm:gap-3 border-t border-white/10"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask Ace anything, in any language…"
          data-testid="assistant-input"
          aria-label="Chat message"
          className="flex-1 min-w-0 bg-[#1a1a1a] border border-white/15 rounded-full px-4 py-3 sm:px-6 sm:py-4 text-sm sm:text-base focus:outline-none focus:border-[#ccff00]/60 placeholder:text-white/35"
        />
        {speech.supported && (
          <button
            type="button"
            onClick={speech.toggle}
            data-testid="assistant-speaker-btn"
            aria-label={speech.enabled ? "Turn off spoken replies" : "Turn on spoken replies"}
            title={speech.enabled ? "Spoken replies ON" : "Spoken replies OFF"}
            className={`w-11 h-11 sm:w-14 sm:h-14 shrink-0 rounded-full flex items-center justify-center transition-all duration-200 ${
              speech.enabled
                ? "bg-[#ccff00]/15 border border-[#ccff00]/70 text-[#ccff00]"
                : "border border-white/25 text-white/70 hover:border-[#ccff00]/70 hover:text-[#ccff00]"
            }`}
          >
            {speech.enabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </button>
        )}
        {supported && (
          <button
            type="button"
            onClick={toggle}
            data-testid="assistant-mic-btn"
            aria-label={listening ? "Stop listening" : "Start voice input"}
            className={`w-11 h-11 sm:w-14 sm:h-14 shrink-0 rounded-full flex items-center justify-center transition-all duration-200 ${
              listening
                ? "bg-red-500 text-white glow-pulse"
                : "border border-white/25 text-white/70 hover:border-[#ccff00]/70 hover:text-[#ccff00]"
            }`}
          >
            <Mic size={18} />
          </button>
        )}
        <button
          type="submit"
          disabled={streaming || !input.trim()}
          data-testid="assistant-send-btn"
          aria-label="Send message"
          className="w-11 h-11 sm:w-14 sm:h-14 shrink-0 rounded-full bg-[#ccff00] text-black flex items-center justify-center disabled:opacity-40 hover:bg-[#a3e635] hover:scale-105 transition-all duration-200"
        >
          <Send size={18} />
        </button>
      </form>
    </main>
  );
}
